import { useEffect, useState } from 'react'
import { Handle, Position } from '@xyflow/react'
import { faceTrackState } from '../../lib/faceTrackState.js'
import { nodeControlClass } from '../../lib/nodeControlClass.js'
import { selectedBorder, activeBorder } from './selectedBorder.js'

/** Node identity color (functional, category-coded — never themed). */
export const FACE_TRACKER_COLOR = '#d060c0'

/**
 * FaceTrackerNode — the webcam as a face-graph source (ENG-1107), the Live
 * Link model: PLUG IT IN and the camera feed becomes the animation data,
 * unplug it and the camera stops. Wiring `anim` toward Final Pose is the
 * user's intent and the permission prompt rides that editor interaction;
 * faceTrackState's watcher (faceGraphReachability) does the arming. Strength
 * comes from the parent blend path (sentinel `__facetrack__`), so a Blend
 * fades the webcam against LiveLink or a clip like any other source.
 *
 * Settings are node data, inline values (Unreal convention):
 *   mirror     — swap Left/Right channels for a mirrored webcam feed
 *   smoothing  — 0 raw .. 1 heavy (one-euro filter cutoff)
 *
 * The status line always says what the camera is doing; blocked/error offer
 * Retry here (and on the viewport pill) because those states never auto-retry.
 *
 * Internal sentinel: data.clipName = '__facetrack__'
 */
export function FaceTrackerNode({ data, selected }) {
  const weight = data._debugWeight ?? 0
  const active = weight > 0.001
  const [, force] = useState(0)
  useEffect(() => faceTrackState.onStateChange(() => force((n) => n + 1)), [])
  const st = faceTrackState.state
  const mirror = data.mirror === true
  const smoothing = Number.isFinite(data.smoothing) ? data.smoothing : 0.5
  const status = st === 'tracking' ? (faceTrackState.faceSeen ? 'camera on · face tracked' : 'camera on · no face')
    : st === 'starting' ? 'starting camera…'
    : st === 'blocked' ? 'camera blocked — allow access, then Retry'
    : st === 'error' ? `camera error: ${faceTrackState.errorMessage}`
    : 'unplugged — wire anim to Final Pose to start the camera'
  const canRetry = st === 'blocked' || st === 'error'
  const stop = (e) => e.stopPropagation()

  return (
    <div style={{
      background: selected ? '#20101c' : '#180a14',
      border: `2px solid ${selected ? selectedBorder('#4a2040') : active ? activeBorder('#4a2040') : '#4a2040'}`,
      borderRadius: 6, padding: '10px 14px', minWidth: 210, color: '#ddd',
      fontFamily: 'var(--m-font)', fontSize: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <span style={{ fontSize: 15 }}>🎥</span>
        <span style={{ color: FACE_TRACKER_COLOR, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>FACE TRACKER</span>
        {active
          ? <span style={{ color: FACE_TRACKER_COLOR, fontSize: 9, marginLeft: 'auto' }}>● {(weight * 100).toFixed(0)}%</span>
          : <span style={{ color: '#604058', fontSize: 9, marginLeft: 'auto' }}>○ idle</span>}
      </div>
      <div style={{ color: '#9a6a90', fontSize: 10, marginBottom: 6 }}>webcam · MediaPipe · ARKit 52</div>
      <div data-testid="face-tracker-node-status" data-state={st}
        style={{ color: canRetry ? 'var(--m-danger)' : '#c090b8', fontSize: 10, marginBottom: 8 }}>{status}</div>
      {canRetry && (
        <button type="button" className={nodeControlClass()} data-testid="face-tracker-node-retry"
          onMouseDown={stop} onClick={() => faceTrackState.retry()}
          style={{ font: 'inherit', fontSize: 10, cursor: 'pointer', width: '100%', padding: '4px 6px', borderRadius: 4,
            border: `1px solid ${FACE_TRACKER_COLOR}`, background: '#2a1424', color: FACE_TRACKER_COLOR, marginBottom: 8 }}>
          Retry camera
        </button>
      )}
      <label onMouseDown={stop} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: '#a07898', marginBottom: 6, cursor: 'pointer', userSelect: 'none' }}>
        <input type="checkbox" className={nodeControlClass()} data-testid="face-tracker-node-mirror" checked={mirror}
          onChange={(e) => data.onChange?.('mirror', e.target.checked)} onMouseDown={stop} />
        Mirror left/right
      </label>
      <label onMouseDown={stop} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: '#a07898', marginBottom: 8, userSelect: 'none' }}>
        Smoothing
        <input type="range" className={nodeControlClass()} min="0" max="1" step="0.05" value={smoothing} data-testid="face-tracker-node-smoothing"
          onChange={(e) => data.onChange?.('smoothing', parseFloat(e.target.value))} onMouseDown={stop}
          style={{ flex: 1 }} />
        <span style={{ width: 28, textAlign: 'right' }}>{smoothing.toFixed(2)}</span>
      </label>
      <div style={{ height: 3, background: '#2a1424', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(weight, 1) * 100}%`, height: '100%', background: active ? FACE_TRACKER_COLOR : '#4a2040', transition: 'width 0.1s linear' }} />
      </div>
      <Handle type="source" position={Position.Right} id="anim"
        style={{ background: active ? FACE_TRACKER_COLOR : '#4a2040', width: 10, height: 10 }} />
    </div>
  )
}
